<div class="wizard-loading" *ngIf="loading" role="status">
  <span class="spinner" aria-hidden="true"></span>
  Loading your Sage records…
</div>

<div class="sage-page" *ngIf="!loading" [class.has-bar]="screen === 'step' || screen === 'review'" [class.touring]="tourOpen">
  <div class="wizard-scroll">
    <ng-container *ngIf="screen === 'hub' || loadError">
      <div class="page-header">
        <div>
          <h1 class="page-title">Connect Sage</h1>
          <p class="page-subtitle">Five quick checks, then your first sync. It takes about 10 minutes.</p>
          <div class="hub-conn">
            <span class="hub-company" *ngIf="sageCompanyName">
              <i class="bi bi-building" aria-hidden="true"></i> Sage company: <strong>{{ sageCompanyName }}</strong>
            </span>
            <span class="pill pill-success"><i class="bi bi-check-circle-fill" aria-hidden="true"></i> Connected</span>
            <span class="pill pill-warning" *ngIf="!syncEnabled"><i class="bi bi-pause-circle" aria-hidden="true"></i> Sync paused</span>
          </div>
        </div>
        <div class="header-right">
          <button type="button" class="help-btn" data-tour="help" *ngIf="!loadError" [class.pulse]="helpPulse" (click)="openTour()">
            <i class="bi bi-question-circle" aria-hidden="true"></i>Help
          </button>
          <div class="user-block" *ngIf="fullName">
            <span class="user-label">Current User:</span>
            <div class="user-name">{{ fullName }}</div>
            <div class="user-tenant">{{ tenantName }}</div>
          </div>
          <div class="overview-menu">
            <button
              id="overviewMenuBtn"
              class="btn-icon-only"
              aria-haspopup="true"
              [attr.aria-expanded]="menuOpen"
              aria-label="Sage integration options"
              (click)="toggleMenu()"
            >
              <i class="bi bi-three-dots-vertical"></i>
            </button>
            <div id="overviewMenu" class="overview-menu-dropdown" *ngIf="menuOpen">
              <button class="menu-item menu-item-danger" [disabled]="disconnecting" (click)="disconnectSage()">
                <i class="bi bi-plug"></i> Disconnect Sage
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="alert-banner" *ngIf="loadError">
        <i class="bi bi-exclamation-circle"></i>
        {{ loadErrorMessage }}
        <button class="btn btn-teal-gradient" (click)="retryLoad()">Retry</button>
      </div>

      <ng-container *ngIf="!loadError">
        <div class="banner banner-ok">
          <i class="bi bi-shield-check"></i>
          <div>
            <strong>Aivor stays in charge.</strong> Your first sync never replaces your Aivor names, codes, costs or
            stock. We only link records that are the same thing.
          </div>
        </div>

        <div class="hub-rows">
          <div
            class="hub-row"
            *ngFor="let step of steps; let i = index; trackBy: trackBySlug"
            [attr.data-tour]="step.slug"
            [style.border-top-color]="step.accent"
          >
            <div class="hub-tile" [style.background]="step.accentSoft" [style.color]="step.accent">
              <i class="bi" [ngClass]="step.icon"></i>
            </div>
            <div class="hub-text">
              <div class="hub-name">{{ i + 1 }}. {{ step.label }}</div>
              <div class="hub-why">{{ step.copy.why }}</div>
            </div>
            <span
              class="pill hub-status"
              [class.pill-success]="stepStates[i] === 'done' || stepStates[i] === 'sent'"
              [class.pill-warning]="stepStates[i] === 'needs'"
            >{{ statusLabel(i) }}</span>
            <ng-container *ngIf="!step.confirmed">
              <button class="btn btn-teal-gradient hub-btn" *ngIf="i === resumeAt" (click)="goToStep(i)">
                {{ stepStates[i] === 'todo' ? 'Start' : 'Continue' }}
              </button>
              <button
                class="btn btn-secondary hub-btn"
                *ngIf="i !== resumeAt"
                [disabled]="!canOpen(i)"
                [attr.title]="canOpen(i) ? null : 'Finish the steps before this one first'"
                (click)="goToStep(i)"
              >
                Open
              </button>
            </ng-container>
          </div>
        </div>

        <div class="hub-actions">
          <button class="btn btn-teal-gradient btn-big" (click)="startOrContinue()">
            {{ hubPrimaryLabel }} <i class="bi bi-arrow-right"></i>
          </button>
        </div>
      </ng-container>
    </ng-container>

    <ng-container *ngIf="screen === 'step' && !loadError">
      <app-wizard-stepper [labels]="stepLabels" [states]="stepStates" [current]="currentIndex"></app-wizard-stepper>

      <div class="page-head">
        <h1 class="page-title">{{ currentStep.label }}</h1>
        <p class="page-subtitle">{{ stepSummaryText }}</p>
      </div>

      <div class="chip-row" *ngIf="!currentStep.confirmed">
        <span class="pill pill-success">{{ stepCounts[currentIndex].linked }} matched</span>
        <span class="pill pill-primary" *ngFor="let label of stepDirections[currentIndex]">{{ label }}</span>
        <span class="pill pill-warning" *ngIf="openCards">{{ openCards }} need{{ openCards === 1 ? 's' : '' }} you</span>
      </div>

      <div class="step-loading" *ngIf="stepLoading" role="status">
        <span class="spinner" aria-hidden="true"></span> Loading your Aivor {{ currentStep.copy.nouns }}…
      </div>

      <ng-container *ngIf="!stepLoading">
        <ng-container *ngIf="cards.length || unitGroupCards.length">
          <div class="tag">{{ openCards ? 'Needs you (' + openCards + ')' : 'Your answers (' + (cards.length + unitGroupCards.length) + ')' }}</div>
          <div class="card-list">
            <ng-container *ngFor="let card of cards; trackBy: trackByKey">
              <app-wizard-pair-card
                *ngIf="card.kind !== 'unit'"
                [kind]="$any(card.kind)"
                [matchId]="card.matchId"
                [noun]="currentStep.copy.noun"
                [sageName]="card.sageName"
                [sageMeta]="card.sageMeta"
                [aivorId]="card.aivorId"
                [aivorName]="card.aivorName"
                [aivorMeta]="card.aivorMeta"
                [reason]="card.reason"
                [choice]="card.choice"
                [answered]="card.answered"
                [saving]="savingIds.has(card.matchId)"
                [records]="recordOptionsFor(currentStep.type, card.isService)"
                [claims]="claims"
                (choose)="answer(card, $event)"
                (pick)="pickRecord(card, $event)"
              ></app-wizard-pair-card>
            </ng-container>
            <app-wizard-unit-group-card
              *ngFor="let group of unitGroupCards; trackBy: trackByKey"
              [group]="group"
              [units]="unitOptions"
              [saving]="groupSaving(group)"
              (groupUnitChange)="chooseGroupUnit(group, $event)"
              (itemUnitChange)="chooseUnit($event, $event.uomId)"
              (itemTypeChange)="setItemType($event, $event.itemTypeId)"
            ></app-wizard-unit-group-card>
            <ng-container *ngFor="let card of cards; trackBy: trackByKey">
              <app-wizard-unit-card
                *ngIf="card.kind === 'unit'"
                [matchId]="card.matchId"
                [name]="card.sageName"
                [code]="card.code"
                [sageUnit]="card.sageUnit"
                [itemTypeId]="card.itemTypeId"
                [isService]="card.isService"
                [units]="unitOptions"
                [selectedUomId]="card.selectedUomId"
                [answered]="card.answered"
                [saving]="savingIds.has(card.matchId)"
                (itemTypeChange)="setItemType(card, $event)"
                (unitChange)="chooseUnit(card, $event)"
              ></app-wizard-unit-card>
            </ng-container>
          </div>
        </ng-container>

        <div class="empty-card" *ngIf="!cards.length && !unitGroupCards.length">
          <div class="empty-icon"><i class="bi bi-check-lg"></i></div>
          <div class="empty-title">{{ currentStep.confirmed ? 'Already sent to Sage' : 'Nothing needs your attention' }}</div>
          <div class="empty-sub">{{ stepSummaryText }}</div>
        </div>

        <div class="banner banner-warn" *ngFor="let warning of bundleWarnings; trackBy: trackByKey">
          <i class="bi bi-exclamation-triangle"></i>
          <div>
            <strong>{{ warning.name }}</strong> is made from
            {{ warning.items.length === 1 ? 'an item that' : warning.items.length + ' items that' }} won't be in Sage
            after the first sync: {{ warning.items.join(', ') }}. It waits in Aivor until
            {{ warning.items.length === 1 ? 'it is' : 'they are' }}.
            <button type="button" class="link-btn" (click)="goToItems()">Go to Items</button>
          </div>
        </div>

        <details class="more" *ngIf="autoUnitRows.length" open>
          <summary>Units matched automatically</summary>
          <ul>
            <li *ngFor="let row of autoUnitRows; trackBy: trackByKey">
              <span class="more-name">{{ row.name }} <span class="muted" *ngIf="row.code">{{ row.code }}</span></span>
              <span class="pill pill-success">{{ row.label }}</span>
            </li>
          </ul>
        </details>

        <details class="more" *ngIf="matchedRows.length">
          <summary>
            Show the {{ matchedRows.length }} {{ matchedRows.length === 1 ? currentStep.copy.noun : currentStep.copy.nouns }}
            that matched
          </summary>
          <ul>
            <li *ngFor="let row of matchedRows | slice: 0 : matchedLimit; trackBy: trackByKey">
              <span class="more-name">
                {{ row.sageName }}
                <span class="muted" *ngIf="row.aivorName">In Aivor: {{ row.aivorName }}</span>
              </span>
              <span class="pill pill-success">Matched</span>
            </li>
            <li *ngIf="matchedRows.length > matchedLimit">
              <span class="muted">…and {{ matchedRows.length - matchedLimit }} more. Review every record to see them all.</span>
            </li>
          </ul>
        </details>

        <details class="more" *ngIf="!currentStep.confirmed">
          <summary>Show what will happen</summary>
          <ul>
            <li><span>Added to Aivor</span><strong>{{ stepCounts[currentIndex].toAivor }}</strong></li>
            <li><span>Sent to Sage</span><strong>{{ stepCounts[currentIndex].toSage }}</strong></li>
            <li><span>Matched</span><strong>{{ stepCounts[currentIndex].linked }}</strong></li>
          </ul>
        </details>

        <div class="review-all" *ngIf="!currentStep.confirmed && currentStep.sessionId">
          <button type="button" class="review-link" (click)="reviewEveryRecord()"><span>Review every record</span></button>
        </div>
      </ng-container>
    </ng-container>

    <ng-container *ngIf="screen === 'review'">
      <div class="page-head">
        <h1 class="page-title">Ready for your first sync</h1>
        <p class="page-subtitle">Here is exactly what will happen. Nothing is sent until you press the button.</p>
      </div>

      <div class="stat3">
        <div class="stat-card">
          <div class="stat-n">{{ reviewCounts.toSage }}</div>
          <div class="stat-text"><strong>Sent to Sage</strong><span class="muted">Aivor records Sage does not have yet</span></div>
        </div>
        <div class="stat-card">
          <div class="stat-n">{{ reviewCounts.toAivor }}</div>
          <div class="stat-text"><strong>Brought into Aivor</strong><span class="muted">Sage records Aivor does not have yet</span></div>
        </div>
        <div class="stat-card">
          <div class="stat-n">{{ reviewCounts.linked }}</div>
          <div class="stat-text"><strong>Already linked</strong><span class="muted">No changes, just connected</span></div>
        </div>
      </div>

      <div class="plain-card safe-card">
        <i class="bi bi-shield-check"></i>
        <div>
          <strong>What will not change</strong>
          <div class="muted">Your Aivor names, codes, categories, costs and stock stay exactly as they are.</div>
        </div>
      </div>

      <label class="plain-card tick-card">
        <input type="checkbox" [(ngModel)]="reviewChecked" />
        I have checked the matches and I am ready to run the first sync
      </label>
    </ng-container>

    <ng-container *ngIf="screen === 'running'">
      <div class="page-head">
        <h1 class="page-title">{{ runFailedAt === null ? 'Syncing with Sage…' : 'The first sync stopped' }}</h1>
        <p class="page-subtitle">
          {{ runFailedAt === null ? 'Please keep this page open. This can take a few minutes.' : 'Nothing after ' + runFailedLabel + ' was sent. Try again when you are ready.' }}
        </p>
      </div>

      <div class="run-card">
        <div class="run-row" *ngFor="let row of runRows; trackBy: trackBySlug" [class.failed]="row.state === 'failed'">
          <span class="run-name">{{ row.label }}</span>
          <div class="progress-track">
            <div
              class="progress-fill"
              [class.indeterminate]="row.state === 'syncing'"
              [class.stopped]="row.state === 'failed'"
              [style.width.%]="row.state === 'waiting' ? 0 : 100"
            ></div>
          </div>
          <span
            class="pill run-pill"
            [class.pill-success]="row.state === 'done' || row.state === 'sent'"
            [class.pill-primary]="row.state === 'syncing'"
            [class.pill-danger]="row.state === 'failed'"
          >{{ runStateLabel(row) }}</span>
          <div class="run-error" *ngIf="row.message" role="alert">{{ row.message }}</div>
        </div>
      </div>

      <div class="run-actions" *ngIf="runFailedAt !== null">
        <button class="btn btn-teal-gradient" (click)="retryRun()"><i class="bi bi-arrow-clockwise"></i> Try again</button>
      </div>
    </ng-container>

    <ng-container *ngIf="screen === 'done'">
      <div class="page-head">
        <h1 class="page-title">You are connected</h1>
        <p class="page-subtitle">Your first sync finished. From now on Aivor and Sage keep each other up to date.</p>
      </div>

      <div class="done-card">
        <div class="empty-icon"><i class="bi bi-check-lg"></i></div>
        <div class="empty-title">First sync complete</div>
        <div class="empty-sub">Last synced just now</div>
      </div>

      <div class="stat3 done-stats">
        <div class="stat-card">
          <div class="stat-n">{{ doneCounts?.toSage ?? 0 }}</div>
          <div class="stat-text"><strong>Sent to Sage</strong></div>
        </div>
        <div class="stat-card">
          <div class="stat-n">{{ doneCounts?.toAivor ?? 0 }}</div>
          <div class="stat-text"><strong>Brought into Aivor</strong></div>
        </div>
        <div class="stat-card">
          <div class="stat-n" [class.stat-warn]="!!needAFix">{{ needAFix ?? '—' }}</div>
          <div class="stat-text">
            <strong>Need a fix</strong>
            <button type="button" class="link-btn" *ngIf="needAFix" (click)="openFixes()">Open "Errors you can fix"</button>
            <span class="muted" *ngIf="needAFix === 0">Nothing to fix</span>
          </div>
        </div>
      </div>

      <div class="banner banner-info">
        <i class="bi bi-info-circle" aria-hidden="true"></i>
        <div>
          <b>Made a wrong match?</b> You can correct any item or supplier link later in
          <button type="button" class="link-btn" (click)="openFixMatches()">Fix matches</button>. Nothing is lost.
        </div>
      </div>

      <div class="done-actions">
        <button class="btn btn-teal-gradient btn-big" (click)="goToConsole()">Go to sync overview</button>
      </div>
    </ng-container>
  </div>

  <div class="wizard-bar" *ngIf="screen === 'step'">
    <button class="btn btn-secondary" (click)="back()"><i class="bi bi-arrow-left"></i> Back</button>
    <span class="bar-hint" *ngIf="openCards > 0 && !busy">
      Answer the question{{ openCards === 1 ? '' : 's' }} above to continue
    </span>
    <span class="bar-hint" *ngIf="busy">Saving…</span>
    <button class="btn btn-teal-gradient" [disabled]="!canGoNext" (click)="next()">
      {{ nextLabel }} <i class="bi bi-arrow-right"></i>
    </button>
  </div>

  <div class="wizard-bar" *ngIf="screen === 'review'">
    <button class="btn btn-secondary" (click)="backFromReview()"><i class="bi bi-arrow-left"></i> Back</button>
    <span class="bar-hint" *ngIf="!reviewChecked">Tick the box above to continue</span>
    <button class="btn btn-teal-gradient" [disabled]="!reviewChecked" (click)="startRun()">
      <i class="bi bi-play-fill"></i> Run first sync
    </button>
  </div>

  <!-- Disconnect confirmation modal -->
  <div class="cp-overlay" *ngIf="showDisconnectConfirm">
    <div class="cp-panel confirm-panel">
      <div class="cp-header">
        <div class="cp-header-tile confirm-icon-tile">
          <i class="bi bi-exclamation-triangle"></i>
        </div>
        <div>
          <h2 class="cp-title">Disconnect Sage?</h2>
          <p class="cp-sub">
            Syncing stops until you reconnect. Your matches and progress are kept, so you can pick
            up exactly where you left off.
          </p>
        </div>
        <button class="cp-close" type="button" aria-label="Close" (click)="cancelDisconnect()">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>

      <div class="cp-footer">
        <button class="btn cp-btn-cancel" (click)="cancelDisconnect()">Cancel</button>
        <button class="btn cp-btn-danger" [disabled]="disconnecting" (click)="confirmDisconnect()">
          {{ disconnecting ? 'Disconnecting…' : 'Disconnect' }}
        </button>
      </div>
    </div>
  </div>
</div>

<app-sage-tour
  *ngIf="tourOpen"
  [steps]="tourSteps"
  [offerHint]="tourOfferHint"
  (outcome)="onTourOutcome($event)"
  (closed)="onTourClosed()"
></app-sage-tour>
